import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { ghs, num, formatDate, formatDateTime } from "../lib/format";
import { ArrowLeft, Printer, GasPump, Truck, Package, CheckCircle, Archive, X, WarningCircle, PencilSimple, Copy, Camera, Image as ImageIcon, Trash, FilePdf, ArrowsOut, FileImage } from "@phosphor-icons/react";

const STATUS_CHIPS = {
  draft:      { cls: "bg-slate-100 text-slate-700 border-slate-200",      label: "Draft" },
  ready:      { cls: "bg-sky-50 text-ps-blue border-sky-200",             label: "Ready" },
  dispatched: { cls: "bg-orange-50 text-ps-orange border-orange-200",     label: "Dispatched" },
  delivered:  { cls: "bg-lime-50 text-lime-700 border-lime-200",          label: "Delivered" },
  filed:      { cls: "bg-indigo-50 text-indigo-700 border-indigo-200",    label: "Filed" },
  cancelled:  { cls: "bg-red-50 text-ps-red border-red-200",              label: "Cancelled" },
};

const TIMELINE = [
  { key: "draft",      label: "Order created",   field: "created_at",    icon: PencilSimple },
  { key: "ready",      label: "Fuel allocated",  field: "ready_at",      icon: GasPump },
  { key: "dispatched", label: "Driver dispatched", field: "dispatched_at", icon: Truck },
  { key: "delivered",  label: "Consignee signed", field: "delivered_at", icon: CheckCircle },
  { key: "filed",      label: "Waybill filed",    field: "filed_at",     icon: Archive },
];

export default function TripDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);  // { type, ... }
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/trips/${id}`);
      setTrip(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const handleAction = async (type, payload) => {
    setErr("");
    try {
      const url = `/trips/${id}/${type}`;
      await api.post(url, payload);
      setAction(null);
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const printCmr = () => {
    const token = localStorage.getItem("access_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/trips/${id}/cmr?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener");
  };

  if (loading) return <div className="p-8"><div className="brand-progress w-1/3" /></div>;
  if (!trip) return <div className="p-8 text-slate-500">{err || "Trip not found."}</div>;

  const chip = STATUS_CHIPS[trip.status] || STATUS_CHIPS.draft;

  const isClientWaybill = trip.waybill_source === "client";
  const primaryRef = isClientWaybill ? (trip.client_waybill_number || trip.cmr_number) : trip.cmr_number;

  // Next action depends on status
  const NEXT_ACTIONS = {
    draft:      { label: "Allocate fuel & make ready", type: "allocate-fuel", icon: GasPump,    color: "bg-ps-blue hover:bg-ps-deep" },
    ready:      { label: "Dispatch driver",            type: "dispatch",      icon: Truck,      color: "bg-ps-orange hover:bg-orange-700" },
    dispatched: { label: "Mark as delivered",          type: "deliver",       icon: CheckCircle,color: "bg-lime-600 hover:bg-lime-700" },
    delivered:  { label: "File waybill (archive)",     type: "file",          icon: Archive,    color: "bg-indigo-600 hover:bg-indigo-700" },
  };
  const next = NEXT_ACTIONS[trip.status];

  return (
    <div data-testid="trip-detail-page">
      <PageHeader
        subtitle={isClientWaybill ? `Client waybill · ${primaryRef}` : trip.cmr_number}
        title={`${trip.sender?.name || "—"} → ${trip.consignee?.name || "—"}`}
        action={
          <div className="flex gap-3">
            <button onClick={() => nav("/trips")} data-testid="back-button" className="border border-slate-300 px-3 py-2.5 rounded-md text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-1.5">
              <ArrowLeft size={14} /> Back
            </button>
            <button
              onClick={() => nav(`/trips?from=${trip.id}`)}
              data-testid="duplicate-trip-button"
              className="border border-slate-300 text-ps-deep px-3 py-2.5 rounded-md text-sm hover:bg-slate-100 flex items-center gap-1.5"
              title="Open a new order pre-filled with this one's details"
            >
              <Copy size={14} weight="bold" /> Duplicate
            </button>
            {trip.status !== "draft" && !isClientWaybill && (
              <button onClick={printCmr} data-testid="print-cmr-button" className="border border-ps-blue text-ps-blue px-4 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-sky-50">
                <Printer size={16} weight="bold" /> Print CMR
              </button>
            )}
            {next && trip.status !== "cancelled" && trip.status !== "filed" && (
              <button
                onClick={() => setAction({ type: next.type })}
                data-testid={`action-${next.type}`}
                className={`text-white px-4 py-2.5 rounded-md font-semibold flex items-center gap-2 transition-colors shadow-sm ${next.color}`}
              >
                <next.icon size={16} weight="bold" /> {next.label}
              </button>
            )}
            {!["filed", "cancelled"].includes(trip.status) && (
              <button onClick={() => setAction({ type: "cancel" })} data-testid="action-cancel" className="border border-red-300 text-ps-red px-3 py-2.5 rounded-md text-sm hover:bg-red-50">
                Cancel
              </button>
            )}
          </div>
        }
      />

      {/* Status & Timeline */}
      <div className="px-8 py-5 bg-white border-b border-slate-200">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <span className={`px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] rounded border ${chip.cls}`}>{chip.label}</span>
          {isClientWaybill ? (
            <span className="px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] rounded border bg-amber-50 text-amber-700 border-amber-200" data-testid="waybill-source-chip-client">
              Client waybill · {trip.client_waybill_issuer || trip.sender?.name || "—"}
            </span>
          ) : (
            <span className="px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] rounded border bg-sky-50 text-ps-blue border-sky-200" data-testid="waybill-source-chip-portside">
              Portside CMR
            </span>
          )}
          {trip.cancel_reason && <span className="text-sm text-ps-red">Reason: {trip.cancel_reason}</span>}
          {trip.file_reference && <span className="text-sm text-slate-600">Filed under: <span className="font-mono-data text-ps-deep font-semibold">{trip.file_reference}</span></span>}
        </div>

        <div className="flex items-start gap-0 overflow-x-auto">
          {TIMELINE.map((step, i) => {
            const done = !!trip[step.field];
            const Icon = step.icon;
            return (
              <div key={step.key} className="flex items-start gap-3 flex-1 min-w-[150px]" data-testid={`timeline-${step.key}`}>
                <div className="flex flex-col items-center">
                  <div className={`w-9 h-9 rounded-full grid place-items-center border-2 ${done ? "bg-ps-blue border-ps-blue text-white" : "bg-white border-slate-300 text-slate-400"}`}>
                    <Icon size={16} weight={done ? "fill" : "bold"} />
                  </div>
                  {i < TIMELINE.length - 1 && <div className={`w-0.5 h-12 ${done && trip[TIMELINE[i+1].field] ? "bg-ps-blue" : "bg-slate-200"}`} />}
                </div>
                <div className="pt-1">
                  <div className={`text-xs font-semibold ${done ? "text-ps-deep" : "text-slate-400"}`}>{step.label}</div>
                  <div className="text-[10px] font-mono-data text-slate-500 mt-0.5">{trip[step.field] ? formatDateTime(trip[step.field]) : "—"}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {err && (
        <div className="mx-8 mt-4 border border-red-300 bg-red-50 text-ps-red text-sm px-3 py-2 rounded-md flex items-center gap-2" data-testid="trip-error">
          <WarningCircle size={16} weight="bold" /> {err}
        </div>
      )}

      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Parties */}
        <Card title="Sender · Consignor">
          <KV k="Name" v={trip.sender?.name} bold />
          <KV k="Address" v={trip.sender?.address} />
          <KV k="Contact" v={trip.sender?.contact} mono />
        </Card>

        <Card title="Consignee">
          <KV k="Name" v={trip.consignee?.name} bold />
          <KV k="Address" v={trip.consignee?.address} />
          <KV k="Contact" v={trip.consignee?.contact} mono />
        </Card>

        <Card title="Route &amp; carrier">
          <KV k="Origin (4)" v={trip.origin} bold />
          <KV k="Destination (3)" v={trip.destination} bold />
          <KV k="Truck" v={trip.vehicle_number} mono accent="text-ps-blue" />
          <KV k="Driver" v={trip.driver_name} />
          <KV k="Order date" v={formatDate(trip.order_date)} mono />
          {(trip.fuel_needed_l || 0) > 0 && trip.status === "draft" && (
            <KV k="Fuel planned" v={`${num(trip.fuel_needed_l)} L`} mono accent="text-amber-700" />
          )}
          {isClientWaybill ? (
            <>
              <KV k="Client waybill №" v={trip.client_waybill_number || "—"} mono bold accent="text-amber-700" />
              <KV k="Issued by" v={trip.client_waybill_issuer || trip.sender?.name} />
              <KV k="Internal ref" v={trip.cmr_number} mono />
            </>
          ) : (
            <KV k="Portside CMR" v={trip.cmr_number} mono accent="text-ps-blue" />
          )}
        </Card>

        {/* Cargo */}
        <Card title="Cargo" wide>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <KV k="Description (9)" v={trip.cargo_description} bold />
            <KV k="Packages (7)" v={trip.packages || "—"} mono />
            <KV k="Packing (8)" v={trip.packing} />
            <KV k="HS code (10)" v={trip.hs_code} mono />
            <KV k="Weight (11)" v={trip.weight_kg ? `${num(trip.weight_kg)} kg` : "—"} mono />
            <KV k="Volume (12)" v={trip.volume_m3 ? `${num(trip.volume_m3)} m³` : "—"} mono />
          </div>
          {trip.special_instructions && <KV k="Special instructions (13)" v={trip.special_instructions} />}
        </Card>

        {/* Fuel */}
        <Card title="Fuel allocation">
          {trip.fuel_dispense_id ? (
            <>
              <KV k="Yard fuel" v={`${num(trip.fuel_litres)} L`} mono accent="text-ps-blue" bold />
              <KV k="Price / L" v={`GHS ${trip.fuel_unit_price.toFixed(2)}`} mono />
              <KV k="Total" v={ghs(trip.fuel_total_ghs)} mono accent="text-lime-700" bold />
            </>
          ) : (
            <div className="text-sm text-slate-500 italic py-4">No fuel allocated yet.</div>
          )}
        </Card>

        {/* Charges */}
        <Card title="Charges &amp; payment">
          <KV k="Payment terms (14)" v={trip.payment_terms} />
          <KV k="Agreed charge (15)" v={trip.agreed_charge_ghs ? ghs(trip.agreed_charge_ghs) : "—"} mono accent="text-lime-700" bold />
          {trip.fuel_total_ghs > 0 && trip.agreed_charge_ghs > 0 && (
            <KV k="Margin over fuel" v={ghs(trip.agreed_charge_ghs - trip.fuel_total_ghs)} mono accent={trip.agreed_charge_ghs - trip.fuel_total_ghs >= 0 ? "text-lime-700" : "text-ps-red"} />
          )}
        </Card>

        {/* Delivery signature */}
        {trip.consignee_signature && (
          <Card title="Delivery confirmation (20)" wide>
            <div className="grid grid-cols-3 gap-3">
              <KV k="Consignee signature" v={trip.consignee_signature} bold />
              <KV k="Place" v={trip.delivery_place} />
              <KV k="Date" v={formatDateTime(trip.delivered_at)} mono />
            </div>
            {trip.consignee_remarks && <KV k="Remarks" v={trip.consignee_remarks} />}
          </Card>
        )}

        {trip.notes && (
          <Card title="Internal notes" wide>
            <div className="text-sm whitespace-pre-line text-slate-700">{trip.notes}</div>
          </Card>
        )}

        {/* Digital filing — photos / scans of the signed CMR */}
        <div className="lg:col-span-3">
          <Attachments tripId={trip.id} cmrNumber={trip.cmr_number} />
        </div>
      </div>

      {/* Action modals */}
      {action && (
        <ActionModal action={action} onClose={() => setAction(null)} onSubmit={handleAction} trip={trip} />
      )}
    </div>
  );
}

function Card({ title, wide, children }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl overflow-hidden ${wide ? "lg:col-span-3" : ""}`}>
      <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
        <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">{title}</div>
      </div>
      <div className="p-5 space-y-2">{children}</div>
    </div>
  );
}

function KV({ k, v, mono, bold, accent }) {
  if (v === undefined || v === null || v === "") v = "—";
  return (
    <div className="text-sm">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{k}</div>
      <div className={`${mono ? "font-mono-data" : ""} ${bold ? "font-semibold" : ""} ${accent || "text-ps-deep"}`}>{v}</div>
    </div>
  );
}

function ActionModal({ action, onClose, onSubmit, trip }) {
  const [form, setForm] = useState(() => {
    if (action.type === "allocate-fuel") return { litres: trip.fuel_litres || trip.fuel_needed_l || "", unit_price: trip.fuel_unit_price || "", issued_by: "" };
    if (action.type === "deliver") return { consignee_signature: "", place: trip.destination || "", consignee_remarks: "" };
    if (action.type === "file") return { file_reference: "", notes: "" };
    if (action.type === "cancel") return { reason: "" };
    return {};
  });

  const titles = {
    "allocate-fuel": "Allocate fuel from the yard",
    dispatch:        "Dispatch driver",
    deliver:         "Confirm delivery",
    file:            "File waybill",
    cancel:          "Cancel order",
  };
  const colors = {
    "allocate-fuel": "bg-ps-blue hover:bg-ps-deep",
    dispatch:        "bg-ps-orange hover:bg-orange-700",
    deliver:         "bg-lime-600 hover:bg-lime-700",
    file:            "bg-indigo-600 hover:bg-indigo-700",
    cancel:          "bg-ps-red hover:bg-red-700",
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit(action.type, form);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center" onClick={onClose} data-testid="action-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="p-6 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">{trip.cmr_number}</div>
            <h2 className="font-display font-extrabold text-2xl tracking-tight text-ps-deep mt-1">{titles[action.type]}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-ps-deep"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-4">
          {action.type === "allocate-fuel" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <FF label="Yard litres *" type="number" step="0.01" value={form.litres} onChange={(v) => setForm({ ...form, litres: v })} required testid="alloc-litres" />
                <FF label="Price / L (GHS) *" type="number" step="0.01" value={form.unit_price} onChange={(v) => setForm({ ...form, unit_price: v })} required testid="alloc-price" />
              </div>
              <FF label="Issued by" value={form.issued_by} onChange={(v) => setForm({ ...form, issued_by: v })} testid="alloc-issued" />
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div>
                <div className="font-mono-data text-2xl tabular text-lime-700 font-semibold">
                  {ghs((parseFloat(form.litres) || 0) * (parseFloat(form.unit_price) || 0))}
                </div>
              </div>
            </>
          )}

          {action.type === "dispatch" && (
            <div className="bg-sky-50 border border-sky-200 rounded-md p-4 text-sm">
              The driver <strong className="text-ps-deep">{trip.driver_name}</strong> picks up the CMR and the allocated fuel
              ({num(trip.fuel_litres)} L). After this step the waybill goes on the road.
              <div className="mt-2 text-xs text-slate-500">Click confirm to mark the order as dispatched right now.</div>
            </div>
          )}

          {action.type === "deliver" && (
            <>
              <FF label="Consignee signed (printed name) *" value={form.consignee_signature} onChange={(v) => setForm({ ...form, consignee_signature: v })} required testid="deliver-sig" placeholder="e.g. Kwame Boateng" />
              <FF label="Place of delivery" value={form.place} onChange={(v) => setForm({ ...form, place: v })} testid="deliver-place" />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Remarks (any condition / shortages)</label>
                <textarea rows={3} value={form.consignee_remarks} onChange={(e) => setForm({ ...form, consignee_remarks: e.target.value })} data-testid="deliver-remarks"
                  placeholder="Received in good order; 700/700 bags counted." className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
              </div>
            </>
          )}

          {action.type === "file" && (
            <>
              <FF label="Filing reference (cabinet / folder)" value={form.file_reference} onChange={(v) => setForm({ ...form, file_reference: v })} testid="file-ref" placeholder="FY/MAY/W3/PG07" />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Filing note</label>
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="file-notes"
                  placeholder="Waybill returned by driver, all 3 copies present." className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
              </div>
            </>
          )}

          {action.type === "cancel" && (
            <FF label="Reason *" value={form.reason} onChange={(v) => setForm({ ...form, reason: v })} required testid="cancel-reason" placeholder="Customer cancelled; truck breakdown; etc." />
          )}
        </div>

        <div className="p-6 border-t border-slate-200 flex gap-3 bg-slate-50">
          <button type="button" onClick={onClose} className="flex-1 border border-slate-300 rounded-md py-2.5 text-sm hover:bg-white bg-white">Cancel</button>
          <button type="submit" data-testid="action-submit" className={`flex-1 rounded-md py-2.5 text-sm font-semibold text-white transition-colors ${colors[action.type]}`}>Confirm</button>
        </div>
      </form>
    </div>
  );
}

function FF({ label, testid, onChange, type = "text", ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</label>
      <input type={type} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" {...props} />
    </div>
  );
}

// --------- Attachments: signed CMR scans / photos ---------
// Browser-side image compression so big phone photos do not bloat Mongo.
async function compressImage(file, maxEdge = 1600, quality = 0.82) {
  if (!file.type.startsWith("image/") || file.type === "image/heic") return file;
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file;
  let { width, height } = bitmap;
  if (width > maxEdge || height > maxEdge) {
    const scale = maxEdge / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  return blob ? new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }) : file;
}

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onerror = () => rej(r.error);
    r.onload = () => {
      const s = r.result;            // data:<mime>;base64,xxxx
      const i = s.indexOf(",");
      res(i >= 0 ? s.slice(i + 1) : s);
    };
    r.readAsDataURL(file);
  });
}

function Attachments({ tripId, cmrNumber }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [err, setErr] = useState("");
  const [lightbox, setLightbox] = useState(null);    // attachment metadata
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : "";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/trips/${tripId}/attachments`);
      setItems(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tripId]);

  const rawUrl = (att) => `${process.env.REACT_APP_BACKEND_URL}/api/trips/${tripId}/attachments/${att.id}/raw?token=${encodeURIComponent(token)}`;

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";                              // allow re-pick of same file
    if (!files.length) return;
    setErr("");
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const isPdf = f.type === "application/pdf";
        const compressed = isPdf ? f : await compressImage(f);
        const data_base64 = await fileToBase64(compressed);
        await api.post(`/trips/${tripId}/attachments`, {
          filename: compressed.name || f.name || "cmr-scan.jpg",
          content_type: compressed.type || f.type,
          data_base64,
        });
        setProgress({ done: i + 1, total: files.length });
      }
      await load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setUploading(false);
      setProgress({ done: 0, total: 0 });
    }
  };

  const del = async (att) => {
    if (!window.confirm(`Delete ${att.filename}?`)) return;
    setErr("");
    try {
      await api.delete(`/trips/${tripId}/attachments/${att.id}`);
      setItems((xs) => xs.filter((x) => x.id !== att.id));
      if (lightbox?.id === att.id) setLightbox(null);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const fmtSize = (n) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
  const isPdf = (att) => att.content_type === "application/pdf";

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="attachments-card">
      <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">CMR scans &amp; proof of delivery</div>
          <div className="text-xs text-slate-500 mt-0.5">Snap or upload the signed CMR — replaces hard-copy filing for <span className="font-mono-data text-ps-deep">{cmrNumber}</span>.</div>
        </div>
        <div className="flex gap-2">
          {/* hidden inputs: camera (mobile) + file picker */}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={onPick}
            className="hidden"
            data-testid="attachment-camera-input"
          />
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
            multiple
            onChange={onPick}
            className="hidden"
            data-testid="attachment-file-input"
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => cameraRef.current?.click()}
            data-testid="attachment-camera-button"
            className="border border-ps-blue text-ps-blue px-3 py-1.5 rounded-md text-sm font-semibold flex items-center gap-1.5 hover:bg-sky-50 disabled:opacity-50"
          >
            <Camera size={14} weight="bold" /> Take photo
          </button>
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            data-testid="attachment-upload-button"
            className="bg-ps-blue text-white px-3 py-1.5 rounded-md text-sm font-semibold flex items-center gap-1.5 hover:bg-ps-deep disabled:opacity-50"
          >
            <ImageIcon size={14} weight="bold" /> Upload files
          </button>
        </div>
      </div>

      {uploading && (
        <div className="px-5 py-3 bg-sky-50 border-b border-sky-100 text-sm text-ps-deep flex items-center gap-3" data-testid="attachment-uploading">
          <div className="brand-progress w-32" />
          Uploading {progress.done} / {progress.total}…
        </div>
      )}
      {err && (
        <div className="px-5 py-2 bg-red-50 border-b border-red-200 text-sm text-ps-red flex items-center gap-2" data-testid="attachment-error">
          <WarningCircle size={14} weight="bold" /> {err}
        </div>
      )}

      <div className="p-5">
        {loading ? (
          <div className="brand-progress w-1/3" />
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-6 text-center border-2 border-dashed border-slate-200 rounded-md" data-testid="attachment-empty">
            No scans yet. Use <span className="font-semibold text-ps-deep">Take photo</span> on a phone, or <span className="font-semibold text-ps-deep">Upload files</span> to add the signed CMR.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="attachment-grid">
            {items.map((att) => (
              <div key={att.id} className="group relative border border-slate-200 rounded-md overflow-hidden bg-slate-50" data-testid={`attachment-${att.id}`}>
                <button
                  type="button"
                  onClick={() => setLightbox(att)}
                  className="block w-full aspect-square overflow-hidden"
                  title="Open"
                >
                  {isPdf(att) ? (
                    <div className="w-full h-full grid place-items-center text-slate-500">
                      <FilePdf size={42} weight="bold" />
                    </div>
                  ) : (
                    <img src={rawUrl(att)} alt={att.filename} className="w-full h-full object-cover" loading="lazy" />
                  )}
                </button>
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => setLightbox(att)}
                    title="Open full size"
                    className="bg-white/90 hover:bg-white text-ps-deep p-1 rounded shadow"
                  >
                    <ArrowsOut size={12} weight="bold" />
                  </button>
                  <button
                    type="button"
                    onClick={() => del(att)}
                    data-testid={`attachment-delete-${att.id}`}
                    title="Delete"
                    className="bg-white/90 hover:bg-red-50 text-ps-red p-1 rounded shadow"
                  >
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
                <div className="px-2 py-1.5 text-[10px] font-mono-data text-slate-600 border-t border-slate-200 bg-white truncate" title={att.filename}>
                  {att.filename}
                </div>
                <div className="px-2 pb-1.5 text-[10px] text-slate-400 flex justify-between">
                  <span>{fmtSize(att.size)}</span>
                  <span>{formatDate(att.uploaded_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox / full-size viewer */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-slate-900/90 flex flex-col" onClick={() => setLightbox(null)} data-testid="attachment-lightbox">
          <div className="p-4 flex items-center justify-between text-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <FileImage size={16} />
              <span className="font-mono-data text-sm">{lightbox.filename}</span>
              <span className="text-xs text-slate-300">· {fmtSize(lightbox.size)} · {formatDateTime(lightbox.uploaded_at)}</span>
            </div>
            <div className="flex gap-2">
              <a
                href={rawUrl(lightbox)}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/30 text-white px-3 py-1.5 rounded-md text-sm hover:bg-white/10"
                data-testid="attachment-open-tab"
              >
                Open in new tab
              </a>
              <button type="button" onClick={() => setLightbox(null)} className="border border-white/30 text-white px-3 py-1.5 rounded-md text-sm hover:bg-white/10" data-testid="attachment-close">
                <X size={14} weight="bold" />
              </button>
            </div>
          </div>
          <div className="flex-1 grid place-items-center p-4" onClick={(e) => e.stopPropagation()}>
            {isPdf(lightbox) ? (
              <iframe title={lightbox.filename} src={rawUrl(lightbox)} className="w-full h-full bg-white rounded" />
            ) : (
              <img src={rawUrl(lightbox)} alt={lightbox.filename} className="max-h-[88vh] max-w-full object-contain" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
